import { zollBaseURL } from '../../../../services/api.service'
import { ZollAPIService } from '../../../../services/zollApi.service'
import { IOption } from '../../../../common/common.types'
import {
  IQaChartSummaryResponse,
  IQaFlagDetailResponse,
  IQaAddendumResponseRequest,
  IQaAddendumResponseResult,
  IQaAddendumReassignRequest,
  IQaAddendumReassignResult,
} from '../store/qaAddendum.types'

const getQaAddendumDetailURL = (flagId: number | string) => `${zollBaseURL}/api/qa-addendums?flagId=${flagId}`
const postQaAddendumResponseURL = () => `${zollBaseURL}/api/qa-addendums/response`
const postQaAddendumReassignURL = () => `${zollBaseURL}/api/qa-addendums/reassign`
const fetchChartSummaryURL = () => `${zollBaseURL}/api/Pcr/get-pcr`

export class QaAddendumService extends ZollAPIService {
  private static activeChartSummaryAbortController: AbortController | null = null

  private static abortActiveChartSummaryRequest() {
    QaAddendumService.activeChartSummaryAbortController?.abort()
  }

  private static createActiveChartSummaryAbortController(): AbortController {
    QaAddendumService.abortActiveChartSummaryRequest()
    const abortController = new AbortController()
    QaAddendumService.activeChartSummaryAbortController = abortController
    return abortController
  }

  private static clearActiveChartSummaryAbortControllerIfCurrent(abortController: AbortController) {
    if (QaAddendumService.activeChartSummaryAbortController === abortController) {
      QaAddendumService.activeChartSummaryAbortController = null
    }
  }

  static cancelChartSummaryRequest() {
    QaAddendumService.abortActiveChartSummaryRequest()
    QaAddendumService.activeChartSummaryAbortController = null
  }

  static isAbortError(error: unknown): boolean {
    return (error as { name?: string })?.name === 'AbortError'
  }

  async fetchQaFlagDetail(flagId: number | string): Promise<IQaFlagDetailResponse> {
    return this.get(getQaAddendumDetailURL(flagId))
  }

  async fetchChartSummary(prid: number): Promise<IQaChartSummaryResponse> {
    const abortController = QaAddendumService.createActiveChartSummaryAbortController()

    try {
      const response = await this.post(
        fetchChartSummaryURL(),
        { Prid: prid, PageName: ['All'] },
        undefined,
        abortController.signal
      )
      const chartSummary = response?.data?.clientData?.[0]?.docData?.entity ?? null
      return { ...response, data: chartSummary }
    } finally {
      QaAddendumService.clearActiveChartSummaryAbortControllerIfCurrent(abortController)
    }
  }

  async submitQaFlagAddendum(flagId: number | string, comments: string): Promise<IQaAddendumResponseResult> {
    const requestBody: IQaAddendumResponseRequest = {
      flagId: Number(flagId),
      comments,
    }
    return this.post(postQaAddendumResponseURL(), requestBody)
  }

  async reassignQaFlag(
    flagId: number | string,
    assignedTo: IOption,
    reason: string
  ): Promise<IQaAddendumReassignResult> {
    const requestBody: IQaAddendumReassignRequest = {
      flagId: Number(flagId),
      assignedTo,
      reason,
    }
    return this.post(postQaAddendumReassignURL(), requestBody)
  }
}
